// frontend/src/pages/WorkshopDashboard.js  (replacement)
// Same data layer as before: assigned vehicles, workshop requests, quotation/invoice
// summaries, invoice list for analytics. Only the presentation changed.

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useAuth } from '../context/AuthContext';
import { getWorkshopRequests } from '../api/requests';
import API_URL from '../api/baseURL';

import useWorkshopHealth from '../hooks/useWorkshopHealth';
import useRequestActions from '../hooks/useRequestActions';
import ScheduleRequestModal from '../components/WorkshopDashboard/ScheduleRequestModal';
import WorkshopStatBar from '../components/WorkshopDashboard/WorkshopStatBar';
import MoneyRow from '../components/WorkshopDashboard/MoneyRow';
import RequestQueue from '../components/WorkshopDashboard/RequestQueue';
import BayLoadGauge from '../components/WorkshopDashboard/BayLoadGauge';
import AssignedVehiclesTable from '../components/WorkshopDashboard/AssignedVehiclesTable';
import WorkshopAnalytics from '../components/WorkshopAnalytics';
import { IconPlus } from '../components/icons/Icons';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const extractQuotationCounts = (qs) => ({
  pendingQuotations: num(qs?.pendingQuotations ?? qs?.pending ?? qs?.pendingQuotes),
  confirmedQuotations: num(qs?.confirmedQuotations ?? qs?.confirmed),
  pendingTotal: num(qs?.pendingTotal),
  confirmedTotal: num(qs?.confirmedTotal),
});

const extractInvoiceCounts = (is) => ({
  unpaidInvoices: num(is?.unpaidInvoices ?? is?.unpaid_count),
  unpaidTotal: num(is?.unpaidTotal ?? is?.unpaid_total),
});

export default function WorkshopDashboard() {
  const navigate = useNavigate();
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  const { token, user } = useAuth();
  const workshopId = user?._id;

  const [invoices, setInvoices] = useState([]);
  const [quotationSummary, setQuotationSummary] = useState(extractQuotationCounts(null));
  const [invoiceSummary, setInvoiceSummary] = useState(extractInvoiceCounts(null));

  const { data: vehicles = [] } = useQuery({
    queryKey: ['assignedVehicles'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/vehicles/assigned/${workshopId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    enabled: !!workshopId && !!token,
  });

  const { data: requests = [] } = useQuery({
    queryKey: ['workshop-requests'],
    queryFn: () => getWorkshopRequests(workshopId, token),
    enabled: !!token && !!workshopId,
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (!token || !workshopId) return;
    (async () => {
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/workshop-quotations/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/workshop-invoices/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/invoices/invoice/list`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (results[0].status === 'fulfilled') setQuotationSummary(extractQuotationCounts(results[0].value?.data));
      if (results[1].status === 'fulfilled') setInvoiceSummary(extractInvoiceCounts(results[1].value?.data));
      if (results[2].status === 'fulfilled') setInvoices(Array.isArray(results[2].value?.data) ? results[2].value.data : []);
    })();
  }, [token, workshopId]);

  const health = useWorkshopHealth({
    vehicles, requests, quotations: quotationSummary, invoices: invoiceSummary,
  });

  // First-response time: hours between request creation and its first quotation.
  const { responseHours, responseSeries } = useMemo(() => {
    const answered = requests.filter(r => r.createdAt && (r.quotedAt || r.respondedAt));
    if (!answered.length) return { responseHours: null, responseSeries: [] };
    const hrs = answered.map(r => (new Date(r.quotedAt || r.respondedAt) - new Date(r.createdAt)) / 36e5);
    const avg = hrs.reduce((s, h) => s + h, 0) / hrs.length;

    // 8 weekly buckets
    const weeks = Array(8).fill(null).map(() => []);
    answered.forEach((r, i) => {
      const wk = 7 - Math.min(7, Math.floor((Date.now() - new Date(r.createdAt)) / (7 * 864e5)));
      if (wk >= 0) weeks[wk].push(hrs[i]);
    });
    return {
      responseHours: Math.round(avg * 10) / 10,
      responseSeries: weeks.map(b => (b.length ? b.reduce((s, h) => s + h, 0) / b.length : 0)),
    };
  }, [requests]);

  const greeting = (() => {
    const h = new Date().getHours();
    const name = user?.businessName || user?.name?.split(' ')[0] || 'Atelier';
    if (h < 12) return `Bonjour ${name}`;
    if (h < 18) return `Bon après-midi ${name}`;
    return `Bonsoir ${name}`;
  })();

  // Was three navigate() calls to /workshop/quotations/new and
  // /workshop/maintenance/new — neither route exists, so every click fell
  // through to the catch-all route.
  const requestActions = useRequestActions();

  return (
    <div className="min-h-screen bg-[#0D0C0B] text-[#F4F1EA] font-['IBM_Plex_Sans',system-ui,sans-serif] antialiased">
      <main className="w-full max-w-[1420px] px-[30px] pt-[26px] pb-[60px] flex flex-col gap-[18px]">

        <header className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-[11px] uppercase tracking-[.2em] text-[#6E665C] mb-1.5">{greeting}</p>
            <h1 className="font-['Space_Grotesk',sans-serif] text-[30px] font-semibold tracking-[-.025em] leading-[1.1]">
              L’atelier aujourd’hui
            </h1>
          </div>
          <div className="flex gap-2.5">
            <button onClick={() => navigate('/workshop/walk-in')}
              className="h-[34px] px-3.5 rounded-[9px] border border-[#2B2724] hover:border-[#4E4740] bg-[#141210]
                         text-[12.5px] font-medium text-[#C9C3B6] hover:text-[#F4F1EA] whitespace-nowrap transition-colors">
              Client sans RDV
            </button>
            <button onClick={() => navigate('/workshop/quotations')}
              className="h-[34px] px-3.5 rounded-[9px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] hover:from-white hover:to-[#DED9CE]
                         text-[#141210] text-[12.5px] font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors">
              <IconPlus className="w-3.5 h-3.5 shrink-0" /> Nouveau devis
            </button>
          </div>
        </header>

        <WorkshopStatBar
          health={health}
          responseHours={responseHours}
          responseSeries={responseSeries}
          onOpenVehicles={() => setShowAllVehicles(true)}
        />

        <MoneyRow health={health} />

        <div className="flex flex-wrap gap-3.5 items-start">
          <RequestQueue
            health={health}
            onAction={requestActions.run}
            busyId={requestActions.busyId}
            error={requestActions.actionError}
            onOpenRequest={() => navigate('/workshop/requests')}
            onOpenAll={() => navigate('/workshop/requests')}
          />
          <div className="flex-1 min-w-0 basis-[316px]">
            <BayLoadGauge health={health} />
          </div>
        </div>

        <AssignedVehiclesTable
          vehicles={vehicles} health={health}
          onLogMaintenance={(v) => navigate(`/workshop/log-maintenance?vehicle=${v._id}`)}
          onCreateQuote={(v) => navigate(`/workshop/quotations?vehicle=${v._id}`)}
          onOpenVehicle={(v) => navigate(`/workshop/history/${v._id}`)}
          onSeeAll={() => setShowAllVehicles((v) => !v)}
          limit={showAllVehicles ? vehicles.length : 6}
        />

        <section className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden">
          <div className="px-[18px] py-3.5 border-b border-[#241F1B]">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Analytiques</p>
          </div>
          <div className="p-[18px]">
            <WorkshopAnalytics requests={requests} invoices={invoices} vehicles={vehicles} />
          </div>
        </section>
      </main>

      <ScheduleRequestModal {...requestActions.scheduleModal} />
    </div>
  );
}
